/**
 * VAE Version Manager
 * Handles version checking and automatic updates for the widget
 */

window.VAE = window.VAE || {};

VAE.VersionManager = (function() {
  'use strict';
  
  // Current widget version - wird beim Build automatisch aktualisiert
  const CURRENT_VERSION = '1.0.0';
  const VERSION_CHECK_INTERVAL = 60 * 60 * 1000; // 1 Stunde
  const VERSION_ENDPOINT = 'https://auth.accessibleai.eu/storage/v1/object/public/cdn/version.json';
  
  let checkInterval = null;
  let isUpdateAvailable = false;
  let latestVersion = null;
  
  // Prüfe Version beim Server
  async function checkForUpdate() {
    try {
      // Füge Timestamp hinzu um Cache zu umgehen
      const response = await fetch(`${VERSION_ENDPOINT}?t=${Date.now()}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      
      if (!response.ok) {
        console.warn('VAE.VersionManager: Could not check for updates');
        return false;
      }
      
      const versionData = await response.json();
      latestVersion = versionData.version;
      
      if (isNewerVersion(latestVersion, CURRENT_VERSION)) {
        console.log(`VAE.VersionManager: New version available: ${latestVersion} (current: ${CURRENT_VERSION})`);
        isUpdateAvailable = true;
        handleUpdateAvailable(versionData);
        return true;
      }
      
      console.log('VAE.VersionManager: Widget is up to date');
      return false;
      
    } catch (error) {
      console.error('VAE.VersionManager: Error checking for updates:', error);
      return false;
    }
  }
  
  // Vergleiche Versionsnummern (semantic versioning)
  function isNewerVersion(newVersion, currentVersion) {
    const parseVersion = (v) => v.split('.').map(n => parseInt(n, 10));
    const newParts = parseVersion(newVersion);
    const currentParts = parseVersion(currentVersion);
    
    for (let i = 0; i < 3; i++) {
      if (newParts[i] > currentParts[i]) return true;
      if (newParts[i] < currentParts[i]) return false;
    }
    
    return false;
  }
  
  // Handle verfügbares Update
  function handleUpdateAvailable(versionData) {
    // Trigger Event für UI-Benachrichtigung
    VAE.events.trigger('update-available', {
      currentVersion: CURRENT_VERSION,
      newVersion: versionData.version,
      releaseNotes: versionData.releaseNotes || null,
      critical: versionData.critical || false
    });
    
    // Bei kritischen Updates: Automatisches Reload nach Warnung
    if (versionData.critical) {
      console.warn('VAE.VersionManager: Critical update detected, will reload in 30 seconds');
      setTimeout(() => {
        if (confirm('Ein wichtiges Update für das Barrierefreiheits-Widget ist verfügbar. Die Seite wird neu geladen.')) {
          reloadWidget();
        }
      }, 30000);
    }
  }
  
  // Widget neu laden mit Cache-Busting
  function reloadWidget() {
    console.log('VAE.VersionManager: Reloading widget...');
    
    // Speichere aktuellen Widget-Status
    const widgetState = {
      isOpen: document.querySelector('.vae-widget-expanded') !== null,
      activePanel: VAE.StateManager?.getState()?.activePanel || null,
      timestamp: Date.now()
    };
    
    // Speichere Status in SessionStorage
    sessionStorage.setItem('vae_reload_state', JSON.stringify(widgetState));
    
    // Finde das aktuelle Script-Tag
    const currentScript = document.querySelector('script[src*="vae.min.js"]');
    if (!currentScript) {
      console.error('VAE.VersionManager: Could not find widget script tag');
      return;
    }
    
    // Erstelle neues Script-Tag mit Cache-Busting
    const newScript = document.createElement('script');
    const newUrl = currentScript.src.split('?')[0] + '?v=' + latestVersion + '&t=' + Date.now();
    newScript.src = newUrl;
    
    // Kopiere data-Attribute
    Array.from(currentScript.attributes).forEach(attr => {
      if (attr.name.startsWith('data-')) {
        newScript.setAttribute(attr.name, attr.value);
      }
    });
    
    // Entferne altes Widget
    if (window.VAE && window.VAE.UI && window.VAE.UI.destroy) {
      window.VAE.UI.destroy();
    }
    
    // Entferne altes Script und füge neues hinzu
    currentScript.remove();
    document.head.appendChild(newScript);
  }
  
  // Restore Widget-Status nach Reload
  function restoreStateAfterReload() {
    const savedState = sessionStorage.getItem('vae_reload_state');
    if (!savedState) return;
    
    try {
      const state = JSON.parse(savedState);
      
      // Prüfe ob Reload kürzlich war (innerhalb von 5 Sekunden)
      if (Date.now() - state.timestamp < 5000) {
        // Warte bis Widget initialisiert ist
        setTimeout(() => {
          if (state.isOpen && VAE.UI && VAE.UI.open) {
            VAE.UI.open();
          }
          
          if (state.activePanel && VAE.StateManager) {
            VAE.StateManager.setState({ activePanel: state.activePanel });
          }
        }, 1000);
        
        // Lösche gespeicherten Status
        sessionStorage.removeItem('vae_reload_state');
        
        // Trigger version update event
        VAE.events.trigger('version-updated');
      }
    } catch (error) {
      console.error('VAE.VersionManager: Error restoring state:', error);
    }
  }
  
  // Alternative: Soft-Update nur für geänderte Module
  async function softUpdate() {
    try {
      // Lade Manifest mit allen Modul-Versionen
      const response = await fetch(`${VERSION_ENDPOINT.replace('version.json', 'manifest.json')}?t=${Date.now()}`);
      const manifest = await response.json();
      
      // Vergleiche Module und lade nur geänderte nach
      for (const module of manifest.modules) {
        if (shouldUpdateModule(module)) {
          await loadModule(module);
        }
      }
      
      // Trigger Update-Complete Event
      VAE.events.trigger('soft-update-complete', { version: latestVersion });
      VAE.events.trigger('version-updated');
      
    } catch (error) {
      console.error('VAE.VersionManager: Soft update failed:', error);
      // Fallback zu hard reload
      reloadWidget();
    }
  }
  
  // Prüfe ob Modul Update braucht
  function shouldUpdateModule(module) {
    const currentModuleVersion = window.VAE.moduleVersions?.[module.name];
    return !currentModuleVersion || isNewerVersion(module.version, currentModuleVersion);
  }
  
  // Lade einzelnes Modul dynamisch
  async function loadModule(module) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `${module.url}?v=${module.version}`;
      script.onload = () => {
        console.log(`VAE.VersionManager: Updated module ${module.name} to version ${module.version}`);
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
  
  // Initialisierung
  function init() {
    console.log(`VAE.VersionManager: Initialized (version ${CURRENT_VERSION})`);
    
    // Restore Status nach möglichem Reload
    restoreStateAfterReload();
    
    // Erste Version-Prüfung nach 1 Minute
    setTimeout(checkForUpdate, 60000);
    
    // Regelmäßige Prüfungen
    checkInterval = setInterval(checkForUpdate, VERSION_CHECK_INTERVAL);
    
    // Event Listener für manuelle Update-Prüfung
    VAE.events.on('check-for-updates', checkForUpdate);
    
    // Event Listener für manuelles Reload
    VAE.events.on('reload-widget', reloadWidget);
  }
  
  // Cleanup
  function destroy() {
    if (checkInterval) {
      clearInterval(checkInterval);
      checkInterval = null;
    }
  }
  
  // Public API
  return {
    init: init,
    destroy: destroy,
    checkForUpdate: checkForUpdate,
    reloadWidget: reloadWidget,
    softUpdate: softUpdate,
    getCurrentVersion: () => CURRENT_VERSION,
    getLatestVersion: () => latestVersion,
    isUpdateAvailable: () => isUpdateAvailable
  };
})(); 